import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  FilterList as FilterListIcon,
  OpenInNew as OpenInNewIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import XLSX from "xlsx-js-style";
import moment from "moment";
import toast from "react-hot-toast";
import "moment/locale/pt-br";

import { useLoading } from "../../../../../shared/context/LoadingContext";
import ClientesAutocomplete from "../../../../../shared/components/clientesAutocomplete/ClientesAutocomplete";
import MultiSelectRelatorio from "../../ui/filtrosPesquisa/MultiSelectRelatorio";
import SelectTriEstado from "../../ui/filtrosPesquisa/SelectTriEstado";
import "../../ui/filtrosPesquisa/FiltrosPesquisa.scss";
import "./RelatorioPesquisaAvaliacoes.scss";

moment.locale("pt-br");

const FILTROS_VAZIOS = {
  // Escolha múltipla
  medicoId: [],
  criadoPor: [],
  estabelecimento: [],
  status: [],
  // Cliente
  clienteId: undefined,
  nomeCliente: "",
  numero: "",
  // Três estados
  terminado: "",
  transformado: "",
  fechado: "",
  debitado: "",
  comOrcamento: "",
};

const CartaoResumo = ({ titulo, valor, nota, cor = "primary.main" }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ py: 1.5 }}>
      <Typography variant="body2" color="textSecondary">
        {titulo}
      </Typography>
      <Typography variant="h5" sx={{ color: cor, fontWeight: "bold" }}>
        {valor}
      </Typography>
      {nota && (
        <Typography variant="caption" color="textSecondary">
          {nota}
        </Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Relatório de pesquisa de avaliações.
 *
 * Lista as avaliações ATIVAS de um período, com filtros de escolha múltipla, e
 * diz de cada uma o que veio depois: quantos procedimentos foram levantados,
 * quantos chegaram a orçamento, e por quanto. É esse último número que
 * responde à pergunta que a clínica faz — avaliou-se muito, deu em quê?
 */
function RelatorioPesquisaAvaliacoes({
  dataFiltro,
  clinicaId,
  auth,
  acao,
  sendRequest,
  atualizacoes,
}) {
  const { startLoading, stopLoading } = useLoading();
  const [dados, setDados] = useState(null);
  const [error, setError] = useState(null);
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [mostrarAgrupamentos, setMostrarAgrupamentos] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [procuraLocal, setProcuraLocal] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);

  const mudar = (campo, valor) =>
    setFiltros((prev) => ({ ...prev, [campo]: valor }));

  /* A lista de clientes serve só o autocomplete. As restantes listas de
     filtros vêm do próprio relatório, tiradas dos dados do período — assim
     incluem quem já saiu da clínica, que a lista de utilizadores ativos não
     dava. */
  useEffect(() => {
    const carregarClientes = async () => {
      try {
        const res = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/clientes/datagrid/${clinicaId}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        );
        setClientes(res.clientes || []);
      } catch (err) {
        toast.error("Não foi possível carregar a lista de clientes.");
      }
    };
    carregarClientes();
  }, [clinicaId, sendRequest, auth.token]);

  const gerarRelatorio = useCallback(async () => {
    setError(null);
    startLoading();
    try {
      const body = {
        clinicaId,
        dataInicio: dataFiltro.dataInicial.format("YYYY-MM-DD"),
        dataFim: dataFiltro.dataFinal.format("YYYY-MM-DD"),
        ...filtros,
      };

      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/avaliacoes/relatorio-pesquisa/${clinicaId}`,
        "POST",
        JSON.stringify(body),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      setDados(resposta);
      setPagina(0);
    } catch (err) {
      setError(err.message || "Erro ao carregar o relatório");
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaId, dataFiltro, filtros, auth.token]);

  /* ── Exportação ───────────────────────────────────────────────────────────
   * Exporta o que está no ecrã, incluindo a procura escrita na caixa: o que
   * se vê é o que sai. Exportar o conjunto inteiro quando a tabela mostrava
   * dez linhas filtradas dava um ficheiro que ninguém reconhecia. */
  const exportarRelatorio = useCallback(() => {
    if (!dados) {
      toast.error("É necessário gerar o relatório primeiro.");
      return;
    }

    const linhas = filtrarLocalmente(dados.avaliacoes, procuraLocal);
    const wb = XLSX.utils.book_new();

    const cabecalho = [
      "Nº",
      "Cliente",
      "Contacto",
      "NIF",
      "Data",
      "Médico",
      "Criado por",
      "Unidade",
      "Status",
      "Terminado",
      "Transformado",
      "Fechado",
      "Debitado",
      "Procedimentos",
      "Orçamentados",
      "Valor estimado",
      "Nº orçamentos",
      "Valor orçamentado",
      "Tratamentos",
      "Categorias",
      "Origem",
      "Motivo de encerramento",
      "Observações",
    ];

    const ws = XLSX.utils.aoa_to_sheet([
      ["Relatório de Pesquisa de Avaliações"],
      [
        `Período: ${dataFiltro.dataInicial.format(
          "DD/MM/YYYY",
        )} a ${dataFiltro.dataFinal.format("DD/MM/YYYY")}`,
      ],
      [`Gerado a ${moment().format("DD/MM/YYYY HH:mm")}`],
      [],
      cabecalho,
      ...linhas.map((a) => [
        a.numero || "-",
        a.cliente?.nome || "-",
        a.cliente?.contacto || "-",
        a.cliente?.nif || "-",
        moment(a.data).format("DD/MM/YYYY"),
        a.medico || "-",
        a.criadoPor || "-",
        a.unidade || "-",
        a.status || "-",
        a.terminado ? "Sim" : "Não",
        a.transformado ? "Sim" : "Não",
        a.fechado ? "Sim" : "Não",
        a.debitado ? "Sim" : "Não",
        a.nrProcedimentos,
        a.nrOrcamentados,
        a.valorEstimado,
        a.orcamentos?.quantidade || 0,
        a.orcamentos?.valor || 0,
        (a.tratamentos || []).join("; "),
        (a.categorias || []).join("; "),
        a.origem || "-",
        a.encerramento
          ? `${a.encerramento.tipo}${
              a.encerramento.descricao ? ` — ${a.encerramento.descricao}` : ""
            }`
          : "-",
        a.observacoes || "-",
      ]),
    ]);

    const estiloCabecalho = {
      font: { bold: true, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: "366092" } },
      alignment: { horizontal: "center" },
    };
    cabecalho.forEach((_, i) => {
      const ref = XLSX.utils.encode_cell({ r: 4, c: i });
      if (ws[ref]) ws[ref].s = estiloCabecalho;
    });
    ws["!cols"] = cabecalho.map((c) => ({ wch: Math.max(12, c.length + 4) }));

    XLSX.utils.book_append_sheet(wb, ws, "Avaliações");

    /* Uma folha de resumo à parte: é o que se leva à reunião. */
    const r = dados.resumo;
    const resumo = [
      ["RESUMO"],
      [],
      ["Avaliações", r.total],
      ["Em aberto", r.emAberto],
      ["Terminadas", r.terminadas],
      ["Transformadas", r.transformadas],
      ["Fechadas", r.fechadas],
      ["Debitadas", r.debitadas],
      [],
      ["Procedimentos levantados", r.nrProcedimentos],
      ["Procedimentos orçamentados", r.nrOrcamentados],
      ["Valor estimado (preço particular)", r.valorEstimado],
      [],
      ["Com orçamento", r.comOrcamento],
      ["Sem orçamento", r.semOrcamento],
      ["Valor orçamentado", r.valorOrcamentado],
      ["Taxa de conversão (%)", r.taxaConversao],
      [],
      ["POR MÉDICO", "Avaliações", "Com orçamento", "Valor orçamentado"],
      ...r.porMedico.map((m) => [
        m.nome,
        m.total,
        m.transformadas,
        m.valorOrcamentado,
      ]),
      [],
      ["POR CRIADOR", "Avaliações", "Com orçamento", "Valor orçamentado"],
      ...r.porCriador.map((m) => [
        m.nome,
        m.total,
        m.transformadas,
        m.valorOrcamentado,
      ]),
      [],
      ["POR UNIDADE", "Avaliações", "Com orçamento", "Valor orçamentado"],
      ...r.porUnidade.map((m) => [
        m.nome,
        m.total,
        m.transformadas,
        m.valorOrcamentado,
      ]),
      [],
      ["POR STATUS", "Avaliações"],
      ...r.porStatus.map((m) => [m.nome, m.total]),
    ];

    const wsResumo = XLSX.utils.aoa_to_sheet(resumo);
    wsResumo["A1"].s = { font: { bold: true, sz: 14 } };
    wsResumo["!cols"] = [{ wch: 38 }, { wch: 14 }, { wch: 16 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb, wsResumo, "Resumo");

    XLSX.writeFile(
      wb,
      `relatorio_avaliacoes_${moment().format("YYYY-MM-DD_HHmm")}.xlsx`,
    );
    toast.success("Ficheiro exportado.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dados, procuraLocal, dataFiltro]);

  useEffect(() => {
    if (acao === "gerar") gerarRelatorio();
    else if (acao === "exportar") exportarRelatorio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acao, atualizacoes]);

  const opcoes = dados?.opcoes || {};
  const linhasVisiveis = useMemo(
    () => filtrarLocalmente(dados?.avaliacoes || [], procuraLocal),
    [dados, procuraLocal],
  );
  const pagina0 = pagina * porPagina;
  const linhasPagina = linhasVisiveis.slice(pagina0, pagina0 + porPagina);

  const moeda = (v) =>
    new Intl.NumberFormat("pt-CV", {
      style: "currency",
      currency: "CVE",
      maximumFractionDigits: 0,
    }).format(v || 0);

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Relatório de Pesquisa de Avaliações
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Mostrar/ocultar filtros">
              <IconButton onClick={() => setMostrarFiltros((v) => !v)}>
                <FilterListIcon />
              </IconButton>
            </Tooltip>
            {dados && (
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={exportarRelatorio}
                variant="contained"
              >
                Exportar Excel
              </Button>
            )}
          </Box>
        </Box>

        {/* ── Filtros ───────────────────────────────────────────────────── */}
        <Collapse in={mostrarFiltros}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 2 }}>
              Filtros
            </Typography>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              <MultiSelectRelatorio
                label="Criado por"
                value={filtros.criadoPor}
                onChange={(v) => mudar("criadoPor", v)}
                opcoes={opcoes.criadores || []}
                largura={260}
              />
              <MultiSelectRelatorio
                label="Médico responsável"
                value={filtros.medicoId}
                onChange={(v) => mudar("medicoId", v)}
                opcoes={opcoes.medicos || []}
                largura={260}
              />
              <MultiSelectRelatorio
                label="Unidade"
                value={filtros.estabelecimento}
                onChange={(v) => mudar("estabelecimento", v)}
                opcoes={opcoes.unidades || []}
                rotuloDe={(u) => `${u.codigo} — ${u.nome}`}
                largura={240}
              />
              <MultiSelectRelatorio
                label="Status"
                value={filtros.status}
                onChange={(v) => mudar("status", v)}
                opcoes={opcoes.status || []}
                chaveDe={(o) => o.valor}
                rotuloDe={(o) => o.valor}
                largura={200}
              />

              <TextField
                size="small"
                label="Nome do cliente"
                placeholder="parte do nome"
                value={filtros.nomeCliente}
                onChange={(e) => mudar("nomeCliente", e.target.value)}
                sx={{ width: 220 }}
                disabled={!!filtros.clienteId}
                helperText={
                  filtros.clienteId ? "a usar o cliente escolhido" : " "
                }
              />

              <ClientesAutocomplete
                className="filtroPesquisa__cliente"
                allClientes={clientes.map((c) => ({
                  label: c.nome,
                  id: c._id,
                }))}
                cliente={
                  filtros.clienteId
                    ? {
                        label:
                          clientes.find((c) => c._id === filtros.clienteId)
                            ?.nome || "",
                        id: filtros.clienteId,
                      }
                    : null
                }
                setCliente={(c) => mudar("clienteId", c?.id || undefined)}
              />

              <TextField
                size="small"
                label="Nº da avaliação"
                value={filtros.numero}
                onChange={(e) => mudar("numero", e.target.value)}
                sx={{ width: 160 }}
              />

              <SelectTriEstado
                label="Terminado"
                value={filtros.terminado}
                onChange={(v) => mudar("terminado", v)}
              />
              <SelectTriEstado
                label="Transformado"
                value={filtros.transformado}
                onChange={(v) => mudar("transformado", v)}
              />
              <SelectTriEstado
                label="Fechado"
                value={filtros.fechado}
                onChange={(v) => mudar("fechado", v)}
              />
              <SelectTriEstado
                label="Debitado"
                value={filtros.debitado}
                onChange={(v) => mudar("debitado", v)}
              />
              <SelectTriEstado
                label="Com orçamento"
                value={filtros.comOrcamento}
                onChange={(v) => mudar("comOrcamento", v)}
                largura={160}
              />
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              {/* O botão de gerar está no topo da página, longe daqui. Repetir
                  o gesto ao lado dos filtros poupa a subida. */}
              <Button variant="contained" onClick={gerarRelatorio}>
                Aplicar filtros
              </Button>
              <Button
                onClick={() => {
                  setFiltros(FILTROS_VAZIOS);
                  setProcuraLocal("");
                }}
              >
                Limpar
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dados?.truncado && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            São {dados.total} avaliações e a lista mostra as primeiras{" "}
            {dados.maxLinhas}. <strong>O resumo conta todas.</strong> Para ver
            as restantes, aperte o período ou use mais filtros.
          </Alert>
        )}

        {/* ── Resumo ────────────────────────────────────────────────────── */}
        {dados && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Avaliações"
                  valor={dados.resumo.total}
                  nota={`${dados.resumo.emAberto} em aberto`}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Com orçamento"
                  valor={dados.resumo.comOrcamento}
                  nota={`${dados.resumo.semOrcamento} sem orçamento`}
                  cor="success.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Taxa de conversão"
                  valor={`${dados.resumo.taxaConversao}%`}
                  nota="avaliações que chegaram a orçamento"
                  cor="warning.main"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <CartaoResumo
                  titulo="Valor orçamentado"
                  valor={moeda(dados.resumo.valorOrcamentado)}
                  nota={`estimado nas avaliações: ${moeda(
                    dados.resumo.valorEstimado,
                  )}`}
                  cor="secondary.main"
                />
              </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="subtitle1">
                  Por médico, criador, unidade e status
                </Typography>
                <IconButton
                  onClick={() => setMostrarAgrupamentos((v) => !v)}
                  size="small"
                >
                  {mostrarAgrupamentos ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )}
                </IconButton>
              </Box>

              <Collapse in={mostrarAgrupamentos}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  {[
                    ["Criado por", dados.resumo.porCriador],
                    ["Médico responsável", dados.resumo.porMedico],
                    ["Unidade", dados.resumo.porUnidade],
                    ["Status", dados.resumo.porStatus],
                  ].map(([titulo, grupos]) => (
                    <Grid item xs={12} md={6} key={titulo}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {titulo}
                      </Typography>
                      <Divider sx={{ mb: 1 }} />
                      {grupos.length === 0 && (
                        <Typography variant="body2" color="textSecondary">
                          Sem dados
                        </Typography>
                      )}
                      {grupos.map((g) => (
                        <Box
                          key={g.nome}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                            py: 0.25,
                          }}
                        >
                          <Typography variant="body2" noWrap>
                            {g.nome}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "nowrap",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {g.total} · {g.transformadas} orç. ·{" "}
                            {moeda(g.valorOrcamentado)}
                          </Typography>
                        </Box>
                      ))}
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Paper>
          </>
        )}

        {/* ── Procura dentro do resultado ───────────────────────────────── */}
        {dados && (
          <TextField
            fullWidth
            size="small"
            placeholder="Procurar no resultado: cliente, nº, médico, criador, tratamento…"
            value={procuraLocal}
            onChange={(e) => {
              setProcuraLocal(e.target.value);
              setPagina(0);
            }}
            sx={{ mb: 2 }}
            InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
          />
        )}

        {/* ── A lista ───────────────────────────────────────────────────── */}
        {!dados && !error && (
          <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
            Escolha o período e aperte «Gerar Relatório».
          </Typography>
        )}

        {dados && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {linhasVisiveis.length} avaliação(ões)
              {procuraLocal ? ` de ${dados.avaliacoes.length} na lista` : ""}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Nº</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Contacto</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Médico</TableCell>
                    <TableCell>Criado por</TableCell>
                    <TableCell>Unidade</TableCell>
                    <TableCell align="center">Proc.</TableCell>
                    <TableCell align="center">Orç.</TableCell>
                    <TableCell align="right">Valor orçamentado</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Tratamentos</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linhasPagina.map((a) => (
                    <TableRow key={a._id} hover>
                      <TableCell>{a.numero || "-"}</TableCell>
                      <TableCell>
                        {a.cliente ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {/* Abre a ficha já na aba da avaliação. */}
                            <MuiLink
                              component={Link}
                              to={a.cliente.link}
                              color="inherit"
                              underline="hover"
                            >
                              {a.cliente.nome}
                            </MuiLink>
                            <Tooltip title="Abrir a avaliação do cliente">
                              <IconButton
                                size="small"
                                component={Link}
                                to={a.cliente.link}
                              >
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{a.cliente?.contacto || "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {moment(a.data).format("DD/MM/YYYY")}
                      </TableCell>
                      <TableCell>{a.medico || "-"}</TableCell>
                      <TableCell>{a.criadoPor || "-"}</TableCell>
                      <TableCell>{a.unidade || "-"}</TableCell>
                      <TableCell align="center">
                        {a.nrOrcamentados}/{a.nrProcedimentos}
                      </TableCell>
                      <TableCell align="center">
                        {a.orcamentos?.quantidade || 0}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontVariantNumeric: "tabular-nums" }}
                      >
                        {moeda(a.orcamentos?.valor)}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}
                        >
                          {a.fechado && (
                            <Chip
                              size="small"
                              label="Fechada"
                              color="default"
                            />
                          )}
                          {a.terminado && (
                            <Chip
                              size="small"
                              label="Terminada"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {a.transformado && (
                            <Chip
                              size="small"
                              label="Transformada"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {a.debitado && (
                            <Chip
                              size="small"
                              label="Debitada"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                          {!a.fechado && !a.terminado && (
                            <Chip
                              size="small"
                              label="Em aberto"
                              color="info"
                              variant="outlined"
                            />
                          )}
                          {a.status && (
                            <Chip size="small" label={a.status} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <Tooltip title={(a.tratamentos || []).join(", ")}>
                          <Typography variant="body2" noWrap>
                            {(a.tratamentos || []).join(", ") || "-"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {linhasVisiveis.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} align="center">
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ py: 3 }}
                        >
                          Nenhuma avaliação com estes filtros.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={linhasVisiveis.length}
              page={pagina}
              onPageChange={(e, nova) => setPagina(nova)}
              rowsPerPage={porPagina}
              onRowsPerPageChange={(e) => {
                setPorPagina(parseInt(e.target.value, 10));
                setPagina(0);
              }}
              labelRowsPerPage="Registos por página"
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
}

/** Procura em tudo o que a linha mostra. Fica fora do componente para não ser
 *  recriada em cada render. */
function filtrarLocalmente(linhas, termo) {
  const t = String(termo || "")
    .trim()
    .toLowerCase();
  if (!t) return linhas;

  return linhas.filter((a) =>
    [
      a.numero,
      a.cliente?.nome,
      a.cliente?.contacto,
      a.cliente?.nif,
      a.medico,
      a.criadoPor,
      a.unidade,
      a.status,
      ...(a.tratamentos || []),
      ...(a.categorias || []),
    ]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(t)),
  );
}

export default RelatorioPesquisaAvaliacoes;
